import { Link, useRouterState } from '@tanstack/react-router';
import {
  Home,
  Inbox,
  LayoutGrid,
  LayoutTemplate,
  ListChecks,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Users,
} from 'lucide-react';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useId, useState, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { durations, springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { useSidebar } from '@/shared/store/sidebar';
import { Counter } from '@/shared/ui/Counter';
import { Tooltip } from '@/shared/ui/Tooltip';
import { useUnreadCount } from '@/features/notifications/useInbox';
import { useSpacesList } from '@/shared/api/generated/spaces/spaces';
import { unwrap } from '@/shared/api/unwrap';
import { SidebarPages } from '@/features/pages/SidebarPages';
import { openCreateSpace } from '@/features/space/createSpaceStore';
import { useCurrentWorkspace } from '@/shared/workspace/useCurrentWorkspace';

import { Brand } from './Brand';
import styles from './Sidebar.module.scss';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

interface NavEntry {
  to: string;
  icon: ReactNode;
  label: string;
  /** Раздел ещё не сделан: показывается, но не ведёт никуда. */
  soon?: boolean;
  /** Показывать число непрочитанных уведомлений. */
  counter?: boolean;
}

/** Разделы вне пространства и внутри него — два разных списка. */
function navFor(slug: string): NavEntry[] {
  if (slug === '') {
    return [
      { to: '/app', icon: <Home />, label: t.nav.home },
      { to: '/app/inbox', icon: <Inbox />, label: t.nav.inbox, soon: true },
    ];
  }

  return [
    { to: `/app/w/${slug}`, icon: <Home />, label: t.nav.home },
    { to: `/app/w/${slug}/my`, icon: <ListChecks />, label: t.nav.myTasks },
    { to: `/app/w/${slug}/members`, icon: <Users />, label: t.nav.members },
    { to: `/app/w/${slug}/inbox`, icon: <Inbox />, label: t.nav.inbox, counter: true },
  ];
}

/** Подпись, которая растворяется при сворачивании и не занимает места. */
function Label({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.span
          className={className}
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.08, duration: durations.fast } }}
          exit={{ opacity: 0, transition: { duration: durations.exitFast } }}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

/**
 * Боковая колонка.
 *
 * Активный пункт и пункт под курсором отмечаются одной плывущей подложкой на
 * каждый — тот же приём, что во вкладках. В свёрнутом виде остаются иконки
 * с подсказками; в выдвижной панели на телефоне сворачивания нет — там
 * колонка и так появляется только по запросу.
 */
export function Sidebar({
  onNavigate,
  collapsible = true,
}: {
  onNavigate?: () => void;
  collapsible?: boolean;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const groupId = useId();
  const [hovered, setHovered] = useState<string | null>(null);
  const stored = useSidebar((state) => state.collapsed);
  const toggle = useSidebar((state) => state.toggle);
  const collapsed = collapsible && stored;
  const { slug, workspace } = useCurrentWorkspace();
  const nav = navFor(slug);
  const spaces = useSpacesList(slug, { query: { select: unwrap, enabled: slug !== '' } });
  const canCreateSpace =
    workspace !== undefined && (workspace.role === 'owner' || workspace.role === 'admin');
  const unread = useUnreadCount(slug);

  return (
    <div className={cx(styles['sidebar'], collapsed && styles['sidebar--collapsed'])}>
      <div className={styles['sidebar__head']}>
        {slug === '' ? <Brand compact={collapsed} /> : <WorkspaceSwitcher compact={collapsed} />}
        {collapsible && (
          <Tooltip content={collapsed ? t.nav.expand : t.nav.collapse} side="right">
            <button
              type="button"
              className={styles['collapse']}
              aria-label={collapsed ? t.nav.expand : t.nav.collapse}
              aria-expanded={!collapsed}
              onClick={toggle}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </button>
          </Tooltip>
        )}
      </div>

      <LayoutGroup id={groupId}>
        <nav
          className={styles['nav']}
          aria-label={t.nav.sections}
          onPointerLeave={() => {
            setHovered(null);
          }}
        >
          {nav.map((entry) => {
            const active = pathname === entry.to;

            const link = (
              <Link
                key={entry.to}
                to={entry.soon ? (slug === '' ? '/app' : `/app/w/${slug}`) : entry.to}
                className={cx(styles['nav__item'], entry.soon && styles['nav__item--soon'])}
                aria-current={active ? 'page' : undefined}
                aria-disabled={entry.soon === true ? true : undefined}
                aria-label={collapsed ? entry.label : undefined}
                onPointerEnter={() => {
                  setHovered(entry.to);
                }}
                onClick={(event) => {
                  if (entry.soon) {
                    event.preventDefault();

                    return;
                  }

                  onNavigate?.();
                }}
              >
                {hovered === entry.to && !active && (
                  <motion.span
                    className={styles['nav__hover']}
                    layoutId={`${groupId}-hover`}
                    transition={springs.rubber}
                    aria-hidden="true"
                  />
                )}
                {active && (
                  <motion.span
                    className={styles['nav__active']}
                    layoutId={`${groupId}-active`}
                    transition={springs.rubber}
                    aria-hidden="true"
                  />
                )}
                <span className={styles['nav__icon']}>{entry.icon}</span>
                <Label show={!collapsed} className={styles['nav__label']}>
                  {entry.label}
                </Label>
                {entry.soon && (
                  <Label show={!collapsed} className={styles['nav__soon']}>
                    {t.common.soon}
                  </Label>
                )}
                {entry.counter === true && unread > 0 && (
                  <span className={styles['nav__counter']}>
                    <Counter value={unread} tone="accent" />
                  </span>
                )}
              </Link>
            );

            // В свёрнутом виде подпись уходит в подсказку.
            return collapsed ? (
              <Tooltip key={entry.to} content={entry.label} side="right">
                {link}
              </Tooltip>
            ) : (
              link
            );
          })}
        </nav>
      </LayoutGroup>

      {slug !== '' && (
        <section className={styles['spaces']} aria-label={t.nav.spaces}>
          <h2 className={styles['spaces__title']}>
            <LayoutGrid />
            <Label show={!collapsed}>{t.nav.spaces}</Label>
            {canCreateSpace && !collapsed && (
              <Tooltip content={t.nav.newSpace} side="right">
                <button
                  type="button"
                  className={styles['spaces__add']}
                  aria-label={t.nav.newSpace}
                  onClick={() => {
                    // На телефоне сайдбар — выдвижная панель: закрыть её,
                    // диалог живёт в оболочке и панель ему не нужна.
                    onNavigate?.();
                    openCreateSpace();
                  }}
                >
                  <Plus />
                </button>
              </Tooltip>
            )}
          </h2>

          <ul className={styles['spaces__list']}>
            {(spaces.data?.items ?? []).map((sp) => {
              const to = `/app/w/${slug}/s/${sp.slug}`;
              const active = pathname.startsWith(to);

              const link = (
                <Link
                  key={sp.id}
                  to={to}
                  className={cx(styles['space'], active && styles['space--active'])}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed ? sp.name : undefined}
                  onClick={onNavigate}
                >
                  <span className={styles['space__dot']} style={{ background: `#${sp.color}` }} />
                  <Label show={!collapsed} className={styles['space__name']}>
                    {sp.name}
                  </Label>
                  {sp.is_template && !collapsed && (
                    <LayoutTemplate
                      className={styles['space__template']}
                      aria-label={t.templates.space.badge}
                    />
                  )}
                </Link>
              );

              return collapsed ? (
                <Tooltip key={sp.id} content={sp.name} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
          </ul>

          {!collapsed && spaces.data !== undefined && (spaces.data.items ?? []).length === 0 && (
            <p className={styles['spaces__empty']}>{t.nav.noSpaces}</p>
          )}
        </section>
      )}

      {slug !== '' && !collapsed && (
        <SidebarPages
          workspace={slug}
          workspaceID={workspace?.id}
          canEdit={workspace !== undefined && workspace.role !== 'guest'}
          onNavigate={onNavigate}
        />
      )}

      <div className={styles['sidebar__foot']}>
        <UserMenu compact={collapsed} />
      </div>
    </div>
  );
}
